import DELIVERY from '../fragments/delivery';

const IMG = '/images/runners';

const page = {
  slug: 'meal-plans-for-runners',
  title: 'Nutrition For Runners',
  hero: {
    title: 'Nutrition For Runners',
    image: `${IMG}/hero.webp`,
  },
  blocks: [
    { type: 'heading', text: 'How The Program Works', align: 'center' },
    {
      type: 'list',
      ordered: true,
      items: [
        'Schedule your initial free discovery call with our sports dietician, Amity. To get started, shoot her an email at: eat.to.run.nutrition@gmail.com. Together with Amity, you will come up with a meal plan that suits your fitness and nutrition aspirations.',
        'Sign up for the monthly subscription below. The meals within this subscription will be tailored to fit your specific needs and goals discussed in that discovery call.',
        'Achieve your goals!',
      ],
    },
    {
      type: 'list',
      heading: 'Good To Know',
      ordered: false,
      items: [
        'Touch base with Amity 2x/month to go over your training schedule and your nutrition needs.',
        'Each session with Amity is $150.',
        `Meals are delivered weekly — ${DELIVERY.short}.`,
        "Total flexibility: you choose how many meals you'd like every week, and specific requirements are discussed with Amity.",
        'Our meals are fresh and clean (no added sugar, small amounts of salt, and high quality oils).',
        'Prep free! Just pop in the microwave for 2 mins if needed and enjoy!',
        'Our menu changes weekly: make any modifications to your personalized recommended menu no later than 12pm Saturday.',
      ],
    },
    {
      type: 'columns',
      tone: 'warm',
      items: [
        {
          heading: 'Delivery',
          rule: true,
          paragraphs: [DELIVERY.line, `We currently serve ${DELIVERY.zones}.`],
        },
        {
          heading: 'Order By',
          rule: true,
          paragraphs: [DELIVERY.cutoffLine, 'Heat meals for 2 minutes in the microwave and enjoy — our packaging is compostable and/or recyclable.'],
        },
      ],
    },
    {
      type: 'split',
      image: `${IMG}/band.webp`,
      imageAlt: 'A runner mid-stride on a city street',
      imageSide: 'left',
      imageAspect: '1 / 1',
      groups: [
        { heading: 'Monthly Nutrition For Runners', paragraphs: [] },
      ],
      accordion: [
        {
          q: 'Description',
          a: [{
            list: [
              '2 calls with Amity — the first to establish your personalized plan, and the second as a mid-month check in (any meal plan adjustments are welcome beyond this, just shoot her an email!)',
              '4 weeks of food tailored to your needs, and whatever changes that may happen during your training.',
            ],
          }],
        },
        {
          q: 'Price Breakdown',
          a: [{
            list: [
              'Breakfast = $16.99',
              'Lunch = $19.99',
              'Dinner = $23.99',
              'Snacks = $6.99 (small); $10.99 (large)',
              'Calls with Amity (beyond the initial free discovery call) = $150',
            ],
          }],
        },
        // The third row used to repeat "Good To Know" — the same delivery and
        // reheating facts as the list above, worded differently. It now lives
        // once, in the Delivery / Order By columns.
      ],
      cta: {
        label: 'Learn More',
        href: 'https://www.getsquarefare.com/this-weeks-meals/p/meals-for-runners-monthly-subscription',
      },
    },
    {
      type: 'split',
      tone: 'warm',
      // The back frame is a stand-in — both /images/runners files are the same
      // portrait. Swap in a race photo when one exists.
      images: [
        { image: `${IMG}/amityruns.webp`, imageAlt: 'Amity Lui running' },
        { image: `${IMG}/amity1.webp`, imageAlt: 'Sports dietitian and marathon runner Amity Lui' },
      ],
      imageSide: 'right',
      groups: [
        {
          heading: ['Meet Your Sports Dietitian, ', { mark: 'Amity' }],
          paragraphs: [
            [
              'Amity Lui is a sports dietician and marathon runner who specializes in working with '
              + 'athletes to optimize nutrition for performance. Her nutritional philosophy is to ',
              { bold: 'eat so you CAN run' },
              ', not the other way around. Nutrition is arguably just as important as the training '
              + 'itself — ',
              { bold: "without proper nutrition, your body's ability to perform and recover is compromised." },
            ],
            [
              'Amity practices a ', { bold: 'non-diet approach to nutrition' },
              ' and hopes to provide the knowledge and experience to ',
              { bold: 'help you fuel effectively, so you can continue to do what you love!' },
            ],
            'When not running or working with athletes, Amity is likely out and about trying new and exciting restaurants, cooking up something delicious at home, hiking, nurturing her indoor jungle, or geeking out on the latest nutrition research.',
          ],
        },
      ],
    },
    // One section paged by arrows — four stacked quotes pushed the rest of
    // the page a long way down.
    {
      type: 'quotes',
      items: [
        {
          text: "Amity is amazing! I worked with her for 3 months while training for Chicago last year and I ran a 49 minute PR and felt great! I used to think that I was fueling enough but boy was I wrong! I learned that I was under eating which led me to hit the wall around mile 18 but not this time! No cramping, stomach issues, no wall, and I actually enjoyed my race! Amity is passionate, knowledgeable, and professional! I highly recommend working with her if you're serious about running.",
          name: 'Amanda L.',
        },
        {
          text: 'Hi Amity! We met a while ago but I just wanted to reach out to let you know that I followed all of your nutrition tips and they worked! It felt like a lot at first but I tested out some products that you recommended and they worked like a charm on race day. I ran my first marathon and it went so much better than I expected! The weather was on the warmer side but 3:28 was my official time and I actually felt good enough after the race to go out and celebrate with my family! Normally I would feel completely drained and unable to function so this was huge for me. I only had 3 long runs left when we met but I was able to trial my nutrition fueling strategy as you suggested and I stuck by it!',
          name: 'Vanessa P.',
        },
        {
          text: 'I was hesitant to work with a dietitian because I never felt like a real runner. I got on a quick call with Amity and was instantly drawn to her. She was very approachable, understanding, and made me feel like an athlete even though I\'m a slower runner. As someone who struggled with weight and still does, she held space for me to share my experiences and was able to guide me through completing my first couch to 5k. She inspired me to keep running and I have since then run a half marathon which is something I never thought I would do! I am so grateful for everything that I learned from her and appreciate that we still keep in touch even after I "graduated" from her program.',
          name: 'John G.',
        },
        {
          text: 'After trying to BQ after 3 attempts, I reached out to Amity to see what I can do to improve my nutrition. She worked with me to help me figure out the missing puzzle piece and I qualified by 9 minutes! I approached her several months before my BQ attempt because I wanted to do things right. Amity took the time to listen to my past attempts and programmed nutrition to align with my coach\'s training plan for me. She genuinely cares about her athletes and she was able to work with my specific dietary restrictions to make sure I was getting my nutrients in. I would recommend working with a dietitian to all runners as it was a game changer! Amity is a pleasure to work with and I feel confident that prioritizing nutrition has taken me places in my running journey.',
          name: 'Jadyn W.',
        },
      ],
    },
    // The ask used to sit unlinked as the last paragraph of Amity's biography,
    // where it read as part of her bio and had nothing to click.
    {
      type: 'text',
      variant: 'display',
      tone: 'warm',
      paragraphs: ['Ready to level up your nutrition for the NYC marathon?'],
    },
    {
      type: 'text',
      paragraphs: [
        'Work with us and Amity to receive your individualized nutrition plan and the corresponding '
        + 'meals delivered directly to your door. We are stoked to help you reach YOUR performance goals!',
      ],
    },
    {
      type: 'ctaGroup',
      actions: [
        { label: 'Get Started', href: 'mailto:eat.to.run.nutrition@gmail.com' },
        { label: 'Contact Us', to: '/contact-form-general', variant: 'outline' },
      ],
    },
  ],
};

export default page;
